import QRCode from 'qrcode';
import { Capacitor } from '@capacitor/core';

/** Lo mínimo de un activo para armar su etiqueta de QR. */
export interface ActivoEtiquetaQr {
  codigoQr: string;
  numero: number;
  nombre: string;
  /** Sede o ubicación donde está: ayuda a encontrar el equipo al escanear la etiqueta. */
  ubicacionNombre?: string | null;
}

const ANCHO = 600;
const ALTO = 640;
const LADO_QR = 440;
const MARGEN_SUPERIOR = 40;

/** Recorta el texto con «…» hasta que entre en el ancho disponible. */
function recortar(ctx: CanvasRenderingContext2D, texto: string, maximo: number): string {
  if (ctx.measureText(texto).width <= maximo) return texto;
  let corte = texto;
  while (corte.length > 1 && ctx.measureText(`${corte}…`).width > maximo) {
    corte = corte.slice(0, -1);
  }
  return `${corte}…`;
}

/**
 * Etiqueta de un activo como imagen PNG: el QR con su número, nombre, ubicación y
 * código debajo, para imprimir y pegar en el equipo. Se arma en el cliente; no se
 * guarda nada en el servidor.
 */
export async function etiquetaQrPng(activo: ActivoEtiquetaQr): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = ANCHO;
  canvas.height = ALTO;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permite generar la etiqueta.');

  const qr = document.createElement('canvas');
  await QRCode.toCanvas(qr, activo.codigoQr, { width: LADO_QR, margin: 1 });

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, ANCHO, ALTO);
  ctx.drawImage(qr, (ANCHO - LADO_QR) / 2, MARGEN_SUPERIOR);

  const yTexto = MARGEN_SUPERIOR + LADO_QR + 56;
  ctx.fillStyle = '#111111';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 40px system-ui, sans-serif';
  ctx.fillText(recortar(ctx, `#${activo.numero}  ${activo.nombre}`, ANCHO - 40), ANCHO / 2, yTexto);

  if (activo.ubicacionNombre) {
    ctx.font = '28px system-ui, sans-serif';
    ctx.fillStyle = '#444444';
    ctx.fillText(recortar(ctx, activo.ubicacionNombre, ANCHO - 40), ANCHO / 2, yTexto + 50);
  }

  return canvas.toDataURL('image/png');
}

/** Etiquetas individuales, una por activo. Se generan una sola vez y se reutilizan en la hoja y el PDF. */
export function etiquetasQrPng(activos: ActivoEtiquetaQr[]): Promise<string[]> {
  return Promise.all(activos.map(etiquetaQrPng));
}

/** Carga una imagen data-URL y espera a que esté lista para dibujarla. */
function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolver, rechazar) => {
    const img = new Image();
    img.onload = () => resolver(img);
    img.onerror = () => rechazar(new Error('No se pudo preparar una etiqueta.'));
    img.src = src;
  });
}

/**
 * Todas las etiquetas ya generadas en una sola imagen, en dos columnas. Es lo que se muestra en el
 * celular, donde no se puede imprimir ni bajar varios archivos.
 */
export async function hojaQrDesde(etiquetas: string[]): Promise<string> {
  if (etiquetas.length === 0) throw new Error('No hay códigos QR para generar.');
  const imagenes = await Promise.all(etiquetas.map(cargarImagen));
  const columnas = Math.min(2, imagenes.length);
  const filas = Math.ceil(imagenes.length / columnas);
  const { width: ancho, height: alto } = imagenes[0]!;
  const canvas = document.createElement('canvas');
  canvas.width = columnas * ancho;
  canvas.height = filas * alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permite generar la imagen.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  imagenes.forEach((img, i) => ctx.drawImage(img, (i % columnas) * ancho, Math.floor(i / columnas) * alto));
  return canvas.toDataURL('image/png');
}

/** Nombre de archivo sin caracteres que los sistemas operativos no admiten. */
function nombreArchivo(activo: ActivoEtiquetaQr): string {
  const nombre = activo.nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `qr-activo-${activo.numero}${nombre ? `-${nombre}` : ''}.png`;
}

/** Descarga la etiqueta de un activo como PNG. */
export async function descargarQrActivo(activo: ActivoEtiquetaQr): Promise<void> {
  const url = await etiquetaQrPng(activo);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombreArchivo(activo);
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
}

function escapar(texto: string): string {
  return texto.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Hoja imprimible con las etiquetas de varios activos, en cuadrícula. Se abre en una
 * ventana nueva y el diálogo de impresión permite guardarla como PDF.
 *
 * La ventana se abre antes de generar las imágenes: si se abriera después, el
 * navegador la bloquearía por no venir de un clic directo.
 */
export async function imprimirHojaQr(activos: ActivoEtiquetaQr[]): Promise<void> {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('El navegador bloqueó la ventana. Permita las ventanas emergentes para imprimir los QR.');

  ventana.document.title = 'Etiquetas QR';
  ventana.document.body.innerHTML = '<p style="font-family:system-ui,sans-serif">Generando etiquetas…</p>';

  // Si una etiqueta falla, se cierra la ventana para no dejar «Generando…» y el error sigue hasta el llamador.
  const imagenes = await etiquetasQrPng(activos).catch((error: unknown) => {
    ventana.close();
    throw error;
  });
  const celdas = imagenes
    .map((src, i) => `<figure><img src="${src}" alt="Etiqueta ${escapar(activos[i]!.codigoQr)}"></figure>`)
    .join('');

  ventana.document.head.innerHTML = `<meta charset="utf-8"><style>
    /* Margen 0 en la página: el navegador no imprime su encabezado (fecha, URL) en ese caso. */
    @page { margin: 0; }
    body { margin: 0; padding: 10mm; font-family: system-ui, sans-serif; }
    .hoja { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6mm; }
    figure { margin: 0; break-inside: avoid; }
    img { width: 100%; height: auto; display: block; }
  </style>`;
  ventana.document.body.innerHTML = `<div class="hoja">${celdas}</div>`;
  // El diálogo de impresión no debe abrirse antes de que las imágenes estén cargadas.
  await Promise.all(
    Array.from(ventana.document.images).map(
      (img) =>
        new Promise<void>((resolver) => {
          if (img.complete) return resolver();
          img.addEventListener('load', () => resolver(), { once: true });
          img.addEventListener('error', () => resolver(), { once: true });
        }),
    ),
  );
  ventana.focus();
  ventana.print();
}

// ─── Celular: guardar, compartir y PDF ────────────────────────────────────────

/** Nombre de archivo para varias etiquetas, o el de la única si es una sola. */
export function nombreArchivoQr(activos: ActivoEtiquetaQr[], extension: 'png' | 'pdf'): string {
  if (activos.length === 1) return nombreArchivo(activos[0]!).replace(/\.png$/, `.${extension}`);
  return `qr-activos-${activos.length}.${extension}`;
}

/** Quita el prefijo `data:…;base64,` de una data-URL. Falla si no lo trae, en vez de devolver basura. */
export function base64DeDataUrl(uri: string): string {
  const corte = uri.indexOf(',');
  if (!uri.startsWith('data:') || corte < 0) throw new Error('El archivo generado no es válido.');
  return uri.slice(corte + 1);
}

/**
 * PDF A4 con las etiquetas (ya generadas) en cuadrícula de 3 columnas, listo para imprimir.
 * Devuelve el contenido en base64.
 */
export async function pdfQrDesde(etiquetas: string[]): Promise<string> {
  if (etiquetas.length === 0) throw new Error('No hay códigos QR para generar.');
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margen = 10;
  const separacion = 6;
  const columnas = 3;
  const ancho = (210 - margen * 2 - separacion * (columnas - 1)) / columnas;
  const alto = ancho * (ALTO / ANCHO);
  const filasPorPagina = Math.floor((297 - margen * 2 + separacion) / (alto + separacion));
  const porPagina = columnas * filasPorPagina;
  etiquetas.forEach((src, i) => {
    if (i > 0 && i % porPagina === 0) pdf.addPage();
    const posicion = i % porPagina;
    const x = margen + (posicion % columnas) * (ancho + separacion);
    const y = margen + Math.floor(posicion / columnas) * (alto + separacion);
    // Sin compresión cada etiqueta pesa ~1 MB y el PDF no se puede compartir cómodamente.
    pdf.addImage(src, 'PNG', x, y, ancho, alto, undefined, 'FAST');
  });
  return base64DeDataUrl(pdf.output('datauristring'));
}

/**
 * Abre la hoja de compartir del sistema con el archivo (guardar en el teléfono, Drive,
 * WhatsApp, imprimir…). Solo en la app instalada. Si el usuario cierra la hoja sin elegir
 * nada, no es un error. Cualquier otro fallo se lanza para que se muestre al usuario.
 * El archivo temporal se borra al terminar, falle o no.
 */
export async function compartirArchivoNativo(nombre: string, base64: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) throw new Error('Compartir solo está disponible en la app.');
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');
  const { uri } = await Filesystem.writeFile({ path: nombre, data: base64, directory: Directory.Cache });
  try {
    await Share.share({ title: nombre, files: [uri] });
  } catch (error) {
    const texto = String((error as { message?: string }).message ?? error);
    if (!/cancel/i.test(texto)) throw new Error(`No se pudo abrir la hoja de compartir. ${texto}`);
  } finally {
    await Filesystem.deleteFile({ path: nombre, directory: Directory.Cache }).catch(() => undefined);
  }
}

/** Descarga un archivo desde el navegador (Chrome, Safari…). No sirve dentro de la app Android. */
export function descargarBase64(nombre: string, base64: string, tipo: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: tipo }));
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
